import React from 'react'
import TetherPortal from 'components/common/TetherPortal'

type TetherPortalProps = React.ComponentProps<typeof TetherPortal>

type ToolTipProps = TetherPortalProps & {
  children?: React.ReactNode
  order?: string
}

export const ToolTip = ({
  children,
  order = 'top bottom left right',
  ...props
}: ToolTipProps) => (
  <TetherPortal order={order} className="ToolTip" {...props}>
    <div className="content">{children}</div>
  </TetherPortal>
)

type TooltipAnchorProps<T extends HTMLElement> = {
  ref: React.RefObject<T>
  onMouseEnter: React.MouseEventHandler<T>
  onMouseLeave: React.MouseEventHandler<T>
}

type TooltipTetherProps<T extends HTMLElement> = {
  open: boolean
  anchorRef: React.RefObject<T>
  speed: number
}

// Generic over the anchor element so callers can spread the returned props
// onto whichever tag they anchor the tooltip to.
export const useTooltip = <T extends HTMLElement = HTMLElement>(
  speed = 200,
  delay = speed,
): [TooltipAnchorProps<T>, TooltipTetherProps<T>] => {
  const [open, setOpen] = React.useState(false)
  const ref = React.useRef<T>(null)
  const timeout = React.useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  )
  const onMouseEnter = React.useCallback(ev => {
    clearTimeout(timeout.current)
    if (!open) {
      timeout.current = setTimeout(() => {
        setOpen(true)
      }, delay)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  const onMouseLeave = React.useCallback(ev => {
    clearTimeout(timeout.current)
    // 200 millisecond delay before hiding.
    timeout.current = setTimeout(() => {
      setOpen(false)
    }, 200)
  }, [])
  return [
    {
      ref,
      onMouseEnter,
      onMouseLeave,
    },
    { open, anchorRef: ref, speed: speed * 1 },
  ]
}

type HelpTextProps = Omit<ToolTipProps, 'children' | 'speed'> & {
  children?: React.ReactNode
  content?: React.ReactNode
  speed?: number
  delay?: number
}

export const HelpText = ({
  children,
  content,
  speed,
  delay,
  ...props
}: HelpTextProps) => {
  const [parentProps, tooltipProps] = useTooltip<HTMLSpanElement>(speed, delay)
  return (
    <span className="HelpText" {...parentProps}>
      <span className="children">{children}</span>
      <ToolTip {...props} {...tooltipProps}>
        {content}
      </ToolTip>
    </span>
  )
}
